import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as RevenueManagementIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { ReactComponent as TickIcon } from '../../../assets/icons/tick-circle-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  FilterRevenueInterFace,
  ManagementStationInterFace,
} from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchAllStations,
  updateSingleStation,
  setRevenueStationFilterData,
  updateRevenueManagementStationFilters,
  fetchRevenueManagementStationFilters,
  disableRevenueManagementStationFilterApplied,
  setSortData,
  refreshAllStations,
} from '../../../redux/slices/RevenueManagementStationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueApi from '../../../utils/api/revenue';

import AddAccountRevenueShare from './AddAccountRevenueShare';
import FilterRevenue from './filter-station-management';
import RevenueManagementToggle from './RevenueManageToggle';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

interface ManagementStationRevenueProperties {
  currentPage: string;
  selectedAccountId: string | number;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

interface EditPercentageProperties {
  id: string;
  value: number | string;
  accountCustomId: string;
  stationId: string;
}

/**
 * ManagementStationRevenue page function component
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function ManagementStationRevenue(properties: Readonly<ManagementStationRevenueProperties>) {
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage, selectedAccountId } = properties;

  const dispatch = useAppDispatch();
  const inputReference = useRef<HTMLSpanElement>(null);
  const {
    stations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueStation);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [editPercent, setEditPercent] = useState<EditPercentageProperties>({
    id: '',
    value: 0,
    accountCustomId: '',
    stationId: '',
  });

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const [addRevenueShareOpen, setAddRevenueShareOpen] = useState<boolean>(false);

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchStationsData = (data: any) => {
    if (!loading) {
      dispatch(fetchAllStations(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchStationsData({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Function to close the add revenue share side panel, optionally showing a
   * success modal and refreshing the table when a save just completed
   * @param {string} [successMessage] - The success message to show, if the panel closed after a successful save
   */
  const handleCloseAddRevenueShare = (successMessage?: string) => {
    setAddRevenueShareOpen(false);
    if (successMessage) {
      handleRefresh();
      setModalView({
        status: true,
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title={successMessage}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={closeModal}
          />
        ),
      });
    }
  };

  /**
   * Function to handle edit percentage
   * @param {any} rowData - The rowData
   */
  const handleEditPercentage = (rowData: ManagementStationInterFace) => {
    setEditPercent({
      id: rowData.id,
      value: rowData.revenuePercentage ? rowData.revenuePercentage : 0,
      accountCustomId: rowData.accountCustomId ?? '',
      stationId: rowData.stationId ?? '',
    });
  };

  /**
   * API call to update percentage
   */
  const handleUpdateRevenuePercentage = async () => {
    const savedPercentage = editPercent.value === '' ? 0 : Number(editPercent.value);
    const cpoPercentage = Math.max(0, 100 - savedPercentage);
    await revenueApi
      .updateRevenueConfigurationPercentage({
        partnerRevenueSharePercentage: savedPercentage,
        cpoRevenueSharePercentage: cpoPercentage,
        accountCustomId: editPercent.accountCustomId,
        stationId: editPercent.stationId,
      })
      .then(() => {
        const updatedAccount: ManagementStationInterFace | undefined = stations.find(
          (station) => station.id === editPercent.id,
        );
        if (updatedAccount) {
          dispatch(
            updateSingleStation({
              ...updatedAccount,
              revenuePercentage: savedPercentage,
            }),
          );
        }
        setEditPercent({
          id: '',
          value: 0,
          accountCustomId: '',
          stationId: '',
        });
        setModalView({
          status: true,
          title: '',
          header: false,
          rightCorner: '',
          notification: true,
          content: (
            <SuccessNotification
              title={t('stationRevenueUpdated')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={closeModal}
            />
          ),
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to discard the in-progress percentage edit without saving
   */
  const handleCancelEditPercentage = () => {
    setEditPercent({
      id: '',
      value: 0,
      accountCustomId: '',
      stationId: '',
    });
  };

  /**
   * Function to discard the in-progress edit when clicking outside the input field
   * @param {MouseEvent} event - The mouse event
   */
  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as Node;
    const clickedActionButtons = (target as HTMLElement).closest?.('.action-button-groups');
    if (
      editPercent.id !== '' &&
      inputReference.current &&
      !inputReference.current.contains(target) &&
      !clickedActionButtons
    ) {
      handleCancelEditPercentage();
    }
  };

  /**
   * Function to handle percentage edit, clamping to 0-100 while preserving decimals.
   * Keeps the field empty while the user is actively clearing it instead of
   * snapping back to 0, so typing a fresh value doesn't concatenate onto a stale "0".
   * @param {string} value - The percentage value
   */
  const handleChangePercentage = (value: string) => {
    if (value === '') {
      setEditPercent({
        ...editPercent,
        value: '',
      });
      return;
    }
    const percentage = Math.min(100, Math.max(0, Number(value) || 0));
    setEditPercent({
      ...editPercent,
      value: percentage,
    });
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView: sidePanelModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleDeleteStationClick,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: stations,
  });
  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof ManagementStationInterFace,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'id',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onDeleteStation={handleDeleteStationClick}
          onSuccess={() => handleEditModeChange(false)}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof ManagementStationInterFace,
      sortAllow: true,
      queryParamsKey: 'accountCustomId',
      queryParamsValue: 'accountCustomId',
      sidePanelComponent: (
        <FacilityDetailsSidePanel
          actionActive
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          onSuccess={() => handleEditModeChange(false)}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationRevenuePercentage'),
      accessorKey: 'revenuePercentage' as keyof ManagementStationInterFace,
      sortAllow: true,
      /**
       * Renders the editable station revenue percentage cell
       * @param {ManagementStationInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) => (
        <div className="table-row-data">
          {editPercent.id === rowData.id ? (
            <span
              role="presentation"
              className="table-percentage-input"
              ref={inputReference}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleUpdateRevenuePercentage();
                } else if (event.key === 'Escape') {
                  handleCancelEditPercentage();
                }
              }}>
              <CustomInput
                type="number"
                name="revenuePercentage"
                placeholder="0"
                value={editPercent.value}
                onChangeValue={(_name, inputValue) => handleChangePercentage(String(inputValue))}
                maxValue={100}
                decimalLimit={2}
                autoFocus
                fontSize="small"
              />
            </span>
          ) : (
            <span>{Number(rowData.revenuePercentage ?? 0).toFixed(2)}</span>
          )}
        </div>
      ),
    },
    {
      label: t('cpoRevenuePercentage'),
      accessorKey: 'cpoRevenuePercentage' as keyof ManagementStationInterFace,
      sortAllow: true,
      /**
       * Renders the read-only CPO revenue percentage cell as the balance of the station share
       * @param {ManagementStationInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) => {
        const stationPercentage =
          editPercent.id === rowData.id ? editPercent.value : (rowData.revenuePercentage ?? 0);
        const cpoPercentage =
          editPercent.id === rowData.id
            ? Math.max(0, 100 - Number(stationPercentage))
            : (rowData.cpoRevenuePercentage ?? 0);

        return (
          <div className="table-row-data">
            <span>{Number(cpoPercentage).toFixed(2)}</span>
          </div>
        );
      },
    },
    {
      label: t('action'),
      accessorKey: '' as keyof ManagementStationInterFace,
      sortAllow: false,
      columnWidth: 60,
      sticky: 'right' as const,
      /**
       * Function for action buttons
       * @param {any} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementStationInterFace) =>
        editPercent.id === rowData.id ? (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleUpdateRevenuePercentage()}
              role="presentation">
              <TickIcon />
            </div>
            <div
              className="action-button-items delete-color"
              onClick={() => handleCancelEditPercentage()}
              role="presentation">
              <CloseIcon />
            </div>
          </div>
        ) : (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleEditPercentage(rowData)}
              role="presentation">
              <EditIcon />
            </div>
          </div>
        ),
    },
  ];

  /**
   * Handle auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAllStations({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handle submit filter function
   * @param {any} filterData - The filter data
   */
  const handleSubmitFilter = (filterData: FilterRevenueInterFace) => {
    dispatch(setRevenueStationFilterData(filterData));
    fetchStationsData({
      ...filterData,
      page: DefaultApiParameters.page,
      limit,
    });
    setModalView((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Filter clear
   */
  const clearFilter = () => {
    dispatch(setRevenueStationFilterData({}));
    fetchStationsData({
      page: DefaultApiParameters.page,
      limit,
    });
  };

  /**
   * User filter function
   */
  const filterFunction = () => {
    setModalView({
      status: true,
      title: t('rolesFilter'),
      header: false,
      content: (
        <FilterRevenue
          closeModal={closeModal}
          handleSubmitFilter={handleSubmitFilter}
          oldValue={filter}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchStationsData({ ...queryParameters, ...value, sortField, sortBy });
  };
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const request: any = { type: 'Station', ...queryParameters };
    if (selectedAccountId) {
      request.selectedAccountId = selectedAccountId;
    }
    if (sortBy) {
      request.sortBy = sortBy;
      request.sortField = sortField;
    }
    revenueApi
      .fetchRevenueConfigurationListExport(request)
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          t('sNo'),
          t('stationName'),
          t('accountID'),
          t('stationRevenuePercentage'),
          t('cpoRevenuePercentage'),
          t('createdAt'),
          t('updatedAt'),
        ];
        const rows = response.data.map((row: ManagementStationInterFace, index: number) => [
          index + 1,
          row.name,
          row.accountCustomId || '-',
          `${row.partnerRevenuePercentage ? ToFixedValue(row.partnerRevenuePercentage) : 0}`,
          `${row.cpoRevenuePercentage ? ToFixedValue(row.cpoRevenuePercentage) : 0}`,
          row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : '-',
          row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-management-station-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-management-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchStationsData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editPercent]);

  useEffect(() => {
    if (isFilterUpdated || stations.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchStationsData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRevenueManagementStationFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (editPercent.id && inputReference.current) {
      inputReference.current.focus(); // Ensure the input is focused after state update
    }
  }, [editPercent.id]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueManagement'),
        icon: <RevenueManagementIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  useEffect(() => {
    if (searchParameters.get('stationId') || searchParameters.get('accountCustomId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('accountCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ModalBox
        open={openStatus}
        title={sidePanelModalView.title}
        content={sidePanelModalView.content}
        closeModal={() => setOpenStatus(false)}
        header={sidePanelModalView.header}
        rightCorner={sidePanelModalView.rightCorner}
      />
      <SidePaneModel
        open={addRevenueShareOpen}
        title=""
        hideHeader
        closePanel={() => setAddRevenueShareOpen(false)}
        content={
          <AddAccountRevenueShare
            closeModal={handleCloseAddRevenueShare}
            revenueType="station"
            open={addRevenueShareOpen}
          />
        }
      />
      <CustomTable
        header={column}
        data={stations}
        serialNo
        created
        lastUpdated
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateRevenueManagementStationFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRevenueManagementStationFilters}
        filterClearFunction={clearFilter}
        filterFunction={filterFunction}
        sizeChangeButtonRequired
        rightTopContent={
          <div className="right-action">
            <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
            <MyButton
              svgIcon={<PlusIcon />}
              label="Revenue Share"
              buttonType="primary"
              onClickFunc={() => setAddRevenueShareOpen(true)}
            />
          </div>
        }
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        tableHeaderVersion="v1"
        refreshRequired
        handleRefresh={handleRefresh}
        exportDataPdfOrCSV={permissionCheck('cpoRevenueExport') ? exportDataPdfOrCSV : undefined}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
      />
    </div>
  );
}

export default ManagementStationRevenue;
